import {
	summarizeAttendanceTeams,
	attendanceCoverage,
} from "@/utils/clubSnapshot";
import {matchDateLabel} from "@/utils/clubSnapshot";
import moment from "moment";
import {useNavigate} from "react-router-dom";

import Page from "@/components/Page";
import {
	DsBar,
	DsEmpty,
	DsHeader,
	DsKpi,
	DsKpiRow,
	DsLink,
	DsMissingSources,
	DsPreviewBadge,
	DsSection,
	DsSkeleton,
	DsStatus,
	DsTable,
} from "@/components/DsKit";

import {useClubSnapshot} from "@/utils/hooks";

// Stato d'esempio per documenti e trasferte, stabile per ogni gara
const sampleStatus = (id, salt) =>
	[...(String(id) + salt)].reduce((n, c) => n + c.charCodeAt(0), 0) % 3;

const SAMPLE_LOGISTICS = [
	{
		id: 1,
		item: "Completi gara e materiale",
		detail: "Kit away da lavare entro venerdì",
		status: "warn",
	},
	{
		id: 2,
		item: "Pasti pre-gara",
		detail: "Convenzione ristorante confermata",
		status: "ok",
	},
	{
		id: 3,
		item: "Tessere e documenti giocatori",
		detail: "2 tessere da ritirare in Lega",
		status: "warn",
	},
];

function DsTeamManagerPage(props) {
	const {handleNotification} = props;
	const navigate = useNavigate();
	const {
		data: s,
		loading,
		reload,
		scope,
	} = useClubSnapshot({
		handleNotification,
	});

	const teams = s?.teams || [];
	const nextWeek = teams
		.flatMap((t) => t.organization.nextWeek.map((m) => ({...m, team: t})))
		.sort((a, b) => new Date(a.startDate) - new Date(b.startDate));
	const sheets = teams
		.flatMap((t) =>
			t.organization.incompleteSheets.map((m) => ({...m, team: t})),
		)
		.sort((a, b) => new Date(b.startDate) - new Date(a.startDate));
	const toOrganize = nextWeek.filter(
		(m) =>
			(m.convocations === 0 && m.daysTo <= 3) ||
			m.timeToConfirm ||
			m.fieldMissing,
	);
	const attendanceSummary = summarizeAttendanceTeams(teams);
	const attendance = attendanceSummary.rate;

	const matchColumns = [
		{
			key: "match",
			label: "Gara",
			render: (m) => (
				<>
					<div className="ds-strong">
						{m.homeClub} – {m.awayClub}
					</div>
					<div className="ds-muted">
						{m.team.name}
						{m.competitionName ? " · " + m.competitionName : ""}
					</div>
				</>
			),
		},
		{
			key: "when",
			label: "Quando",
			render: (m) => matchDateLabel(m, "ddd D MMM"),
		},
		{
			key: "time",
			label: "Orario",
			render: (m) =>
				m.timeToConfirm ? (
					<DsStatus status="warn" label="Da confermare" />
				) : (
					<DsStatus status="ok" label="Confermato" />
				),
		},
		{
			key: "field",
			label: "Campo",
			render: (m) =>
				m.side === "away" ? (
					<span className="ds-muted">In trasferta</span>
				) : m.fieldMissing ? (
					<DsStatus status="warn" label="Non indicato" />
				) : (
					<DsStatus status="ok" label={m.fieldName || "Indicato"} />
				),
		},
		{
			key: "convocations",
			label: "Convocati",
			align: "center",
			render: (m) =>
				m.convocations === null ? (
					"Da verificare"
				) : m.convocations ? (
					<DsStatus status="ok" label={m.convocations} />
				) : (
					<DsStatus
						status={m.daysTo <= 1 ? "alert" : m.daysTo <= 3 ? "warn" : "none"}
						label="Da diramare"
					/>
				),
		},
		{
			key: "trip",
			label: "Trasferta",
			render: (m) =>
				m.side === "home" ? (
					<span className="ds-muted">—</span>
				) : (
					<>
						<DsStatus
							status={["ok", "warn", "alert"][sampleStatus(m.id, "trip")]}
							label={
								["Organizzata", "Pullman da confermare", "Da organizzare"][
									sampleStatus(m.id, "trip")
								]
							}
						/>{" "}
						<DsPreviewBadge />
					</>
				),
		},
		{
			key: "docs",
			label: "Distinta e documenti",
			render: (m) => (
				<>
					<DsStatus
						status={["ok", "warn", "warn"][sampleStatus(m.id, "docs")]}
						label={
							["Pronti", "Da preparare", "Tessere mancanti"][
								sampleStatus(m.id, "docs")
							]
						}
					/>{" "}
					<DsPreviewBadge />
				</>
			),
		},
	];

	return (
		<Page>
			<div className="ds">
				<DsHeader
					title="Team manager"
					subtitle="Organizzazione gare, convocazioni, presenze e trasferte"
					snapshot={s}
					onReload={reload}
					reloading={loading}
					scope={scope}
					area="teammanager"
					actions={
						<button
							className="ds-btn ds-btn--ghost"
							onClick={() => navigate("/match-day")}
						>
							Giorno gara
						</button>
					}
				/>
				<DsSkeleton visible={loading && !s} />
				{s && (
					<>
						<DsMissingSources sources={s.missingSources} />
						<DsKpiRow>
							<DsKpi
								label="Gare uniche in 7 giorni"
								value={new Set(nextWeek.map((match) => match.id)).size}
								sub={nextWeek.length + " partecipazioni delle rose"}
							/>
							<DsKpi
								label="Da sistemare"
								value={toOrganize.length}
								tone={toOrganize.length ? "yellow" : "green"}
								sub="orari, campi, convocazioni · per partecipazione"
							/>
							<DsKpi
								label="Presenze allenamenti"
								value={
									attendance === null ? "—" : Math.round(attendance * 100) + "%"
								}
								tone={
									attendanceSummary.unknown > 0 || attendance === null
										? undefined
										: attendance >= 0.85
											? "green"
											: attendance >= 0.75
												? "yellow"
												: "red"
								}
								sub={"14 giorni · " + attendanceCoverage(attendanceSummary)}
							/>
							<DsKpi
								label="Distinte da completare"
								value={sheets.length}
								tone={sheets.length ? "yellow" : undefined}
								sub="partecipazioni delle rose negli ultimi 30 giorni"
							/>
							<DsKpi
								label="Trasferte"
								value={nextWeek.filter((m) => m.side === "away").length}
								sub="partecipazioni in trasferta nei prossimi 7 giorni"
							/>
						</DsKpiRow>

						<DsSection
							title="Prossime gare: checklist organizzativa"
							hint="Una voce per rosa e lato della gara; trasferte e documenti sono in anteprima"
							action={<DsLink to="/calendar">Calendario</DsLink>}
						>
							<DsTable
								columns={matchColumns}
								rows={nextWeek}
								rowKey={(match) =>
									match.id + ":" + match.team.id + ":" + match.side
								}
								onRowClick={(m) => navigate("/ds/gara/" + m.id)}
								empty="Nessuna gara nei prossimi 7 giorni."
							/>
						</DsSection>

						<div className="ds-two-cols">
							<DsSection
								title="Presenze agli allenamenti"
								hint="Ultimi 14 giorni fino a oggi · sole sedute concluse, ospiti inclusi; totale delle partecipazioni nelle rose selezionate"
								action={<DsLink to="/trainings">Allenamenti</DsLink>}
							>
								<DsTable
									columns={[
										{
											key: "name",
											label: "Squadra",
											render: (t) => <b>{t.name}</b>,
										},
										{
											key: "sessions",
											label: "Sedute",
											align: "center",
											render: (t) =>
												t.organization.attendance.sessions +
												" concluse · " +
												t.organization.attendance.scheduled +
												" programmate/in corso",
										},
										{
											key: "rate",
											label: "Presenza",
											render: (t) => {
												const r = t.organization.attendance.rate;
												return r === null ? (
													<span className="ds-muted">Nessun dato</span>
												) : (
													<div
														style={{
															display: "flex",
															alignItems: "center",
															gap: 8,
														}}
													>
														<DsBar
															value={r}
															tone={
																t.organization.attendance.unknown > 0
																	? undefined
																	: r >= 0.85
																		? "green"
																		: r >= 0.75
																			? "yellow"
																			: "red"
															}
														/>
														<span>
															{Math.round(r * 100)}% ·{" "}
															{attendanceCoverage(t.organization.attendance)} ·{" "}
															{t.organization.attendance.absent} assenti ·{" "}
															{t.organization.attendance.injured} infortunati
														</span>
													</div>
												);
											},
										},
										{
											key: "absentees",
											label: "Assenze segnate",
											render: (t) =>
												t.organization.attendance.absentees.length ? (
													t.organization.attendance.absentees
														.slice(0, 3)
														.map((a) => `${a.player.lastName} (${a.count})`)
														.join(", ")
												) : (
													<span className="ds-muted">—</span>
												),
										},
									]}
									rows={teams}
								/>
							</DsSection>

							<DsSection
								title="Gare da chiudere"
								hint="Distinte senza presenze segnate"
							>
								{sheets.length ? (
									<div className="ds-list">
										{sheets.map((m) => (
											<div
												key={m.id + m.team.id}
												className="ds-list__item is-clickable"
												onClick={() => navigate("/ds/gara/" + m.id)}
											>
												<div className="ds-list__main">
													<div className="ds-list__title">
														{m.homeClub} – {m.awayClub}
													</div>
													<div className="ds-list__sub">
														{m.team.name} ·{" "}
														{moment(m.startDate).format("ddd D MMM")}
													</div>
												</div>
												<DsStatus status="warn" label="Da completare" />
											</div>
										))}
									</div>
								) : (
									<DsEmpty>Tutte le distinte sono complete.</DsEmpty>
								)}
							</DsSection>
						</div>

						<DsSection title="Materiale e logistica" preview>
							<div className="ds-list">
								{SAMPLE_LOGISTICS.map((m) => (
									<div key={m.id} className="ds-list__item">
										<div className="ds-list__main">
											<div className="ds-list__title">{m.item}</div>
											<div className="ds-list__sub">{m.detail}</div>
										</div>
										<DsStatus status={m.status} />
									</div>
								))}
							</div>
						</DsSection>
					</>
				)}
			</div>
		</Page>
	);
}

export default DsTeamManagerPage;
